import { useParams } from "react-router-dom"
import { Box, Button, useTheme, Typography, Divider, List } from "@mui/material"
import LanguageIcon from "@mui/icons-material/Language"

import { useEventDetail } from "hooks/event/useEventDetail"
import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"

import { Head } from "components/Head"
import { BackIconButton } from "components/Button"
import { MotionDiv, showContent } from "components/motions"
import { InfoListItem } from "components/ListItem"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { Image } from "components/Image"
import { CalendarIcon, LocationIcon } from "../../components/icons"

import { PageLayout } from "extensions/one-siam/layouts/PageLayout"
import {
  Header,
  HeaderSearchButton,
} from "extensions/one-siam/components/Header"
import { AppNavigation } from "extensions/one-siam/container/AppNavigation"
import { getDurationDate } from "utils/date"
import {
  RestrictContentPermissionPrompt,
  RestrictContentPrompt,
} from "components/domains/restrict-content"
export const EventDetail = ({ Layout = PageLayout }) => {
  const theme = useTheme()
  const { goBack } = useAppUI()
  const { t } = useTranslation()
  const params = useParams()
  const { eventId } = params
  const {
    event,
    navigateToLocation,
    openWebsiteLink,
    hasGeolocationPermission,
  } = useEventDetail(eventId)
  const {
    root,
    button,
    buttonContainer,
    card,
    detailContent,
    title,
    imageWrapper,
    detailWrapper,
    contentSecondaryText,
    contentTextWrapper,
    infoContainer,
    infoListItem,
    divider,
    headerWrapper,
    image,
    linkButton,
    pageWrapper,
    coverBanner,
    contentWrapper,
    navigationWrapper,
  } = theme.components[`EventDetailPage`]?.styleOverrides || {}

  if (!event) return null

  return (
    <>
      <Head title={`${t(event.name)} (${t("Event Detail")})`} />
      <Layout
        header={
          <Box sx={headerWrapper}>
            <Header
              title={t("Events & Activities")}
              backButton={<BackIconButton onClick={goBack} />}
              search={<HeaderSearchButton />}
            />
          </Box>
        }
      >
        <Box sx={root}>
          <Box sx={pageWrapper}>
            {!event ? null : (
              <MotionDiv
                animate="animate"
                initial="initial"
                variants={showContent}
              >
                <Box
                  sx={{
                    ...coverBanner,
                    backgroundImage: `url(${event.coverImage})`,
                  }}
                />
                <MotionDiv sx={contentWrapper}>
                  <Box sx={card}>
                    <Box sx={imageWrapper}>
                      <Image sx={image} src={event.coverImage} />
                    </Box>
                    <Box sx={detailWrapper}>
                      <Box sx={headerWrapper}>
                        <Typography sx={title} variant="title" component={"h2"}>
                          {t(event.name)}
                        </Typography>
                      </Box>
                      <Box sx={detailContent}>
                        <List sx={infoContainer}>
                          {event.hasLocation && (
                            <InfoListItem
                              sx={infoListItem}
                              icon={<LocationIcon />}
                              primary={
                                <LocationInfo
                                  feature={event.feature}
                                  component="span"
                                />
                              }
                            />
                          )}
                          <InfoListItem
                            sx={infoListItem}
                            icon={<CalendarIcon />}
                            primary={getDurationDate(
                              event.startDate,
                              event.endDate,
                              { monthFormat: "MMMM" }
                            )}
                          />
                        </List>
                        <Divider sx={divider} />

                        <Box sx={contentTextWrapper}>
                          <Typography
                            variant="body2"
                            sx={contentSecondaryText}
                            dangerouslySetInnerHTML={{
                              __html: event.descriptionText,
                            }}
                          ></Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </MotionDiv>
              </MotionDiv>
            )}
            <RestrictContentPrompt
              open={!event.isFullContent && hasGeolocationPermission}
            />
            <RestrictContentPermissionPrompt
              open={event.locationAccessRequired && !hasGeolocationPermission}
            />
          </Box>
        </Box>
        <Box sx={navigationWrapper}>
          <Box sx={buttonContainer}>
            {!!event.websiteLink && (
              <Button
                variant="outlined"
                sx={linkButton}
                onClick={openWebsiteLink}
                startIcon={<LanguageIcon />}
              ></Button>
            )}
            {event.hasLocation && (
              <Button
                variant="contained"
                sx={button}
                onClick={navigateToLocation}
              >
                {t("View Location")}
              </Button>
            )}
          </Box>
          <AppNavigation />
        </Box>
      </Layout>
    </>
  )
}
